import { useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { usersApi } from '@/api/users'
import { navGroupsFor } from '@/components/layout/navigation'
import { useAuth } from '@/features/auth/useAuth'
import { PEOPLE_STALE_MS, peopleKeys } from './people'

/**
 * Loads the team and customer lists once, as soon as the signed-in workspace mounts, and keeps them
 * in the shared query cache. The Team and Customers pages (and anything else using `useTeam` /
 * `useCustomers`) then render from that cache instead of fetching on every visit; mutations that
 * invalidate `['people']` refresh it in place.
 *
 * Only the lists the user's navigation actually offers are loaded, so a customer never requests them.
 */
export function usePeoplePreload() {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const routes = navGroupsFor(user)
    .flatMap((group) => group.items)
    .map((item) => item.to)
  const wantsTeam = routes.includes('/team')
  const wantsCustomers = routes.includes('/customers')

  useEffect(() => {
    if (wantsTeam) void queryClient.prefetchQuery({ queryKey: peopleKeys.team, queryFn: usersApi.team, staleTime: PEOPLE_STALE_MS })
    if (wantsCustomers) {
      void queryClient.prefetchQuery({ queryKey: peopleKeys.customers, queryFn: usersApi.customers, staleTime: PEOPLE_STALE_MS })
    }
  }, [queryClient, wantsTeam, wantsCustomers])
}
